/**
 * CopilotPage - Enterprise 2-panel layout
 *
 * Artifact list lives in the DashboardLayout sidebar (replaces nav on /copilot).
 * Desktop: Workspace (flex:1) | Chat (420px).
 * Below `md`: tabs switch full-width Chat vs Results (workspace) so charts/tables have room.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  alpha,
  Badge,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { AppDialog } from '../../components/dialog';
import ArrowBackIosNewRounded from '@mui/icons-material/ArrowBackIosNewRounded';
import BarChartRounded from '@mui/icons-material/BarChartRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import InsertChartOutlined from '@mui/icons-material/InsertChartOutlined';
import PushPinRounded from '@mui/icons-material/PushPinRounded';
import SpeedRounded from '@mui/icons-material/SpeedRounded';
import TableChartOutlined from '@mui/icons-material/TableChartOutlined';

import { useCopilotStore } from './stores/useCopilotStore';
import type { CopilotMessage, SQLApproval, WorkspaceArtifact, QueryResultTab, ChartArtifact, MetricArtifact } from './stores/useCopilotStore';
import { useCopilotStream } from './hooks/useCopilotStream';
import { useCopilotHistory } from './hooks/useCopilotHistory';
import WorkspacePanel, { ArtifactCanvas } from './components/WorkspacePanel';
import ChatPanel from './components/ChatPanel';
import AddToDashboardModal from '../../components/AddToDashboardModal';
import { buildApiUrl, tenantApiPrefix } from '../../config';
import { trackWidgetAdded } from '../../utils/analytics';
import { markProductStep, useProductOnboarding } from '../../hooks/useProductOnboarding';
import { SnackbarContext } from '../../ui/SnackbarProvider';

type MobileCopilotPanel = 'chat' | 'workspace';
type MobileResultsView = 'list' | 'detail';

function mobileArtifactCardMeta(tab: WorkspaceArtifact, theme: Theme) {
  const isDark = theme.palette.mode === 'dark';
  const subtle = (main: string) => alpha(main, isDark ? 0.16 : 0.12);
  switch (tab.type) {
    case 'query_result':
      return {
        Icon: TableChartOutlined,
        typeLabel: 'Table',
        iconColor: theme.palette.primary.main,
        iconBg: subtle(theme.palette.primary.main),
      };
    case 'chart':
      return {
        Icon: BarChartRounded,
        typeLabel: 'Chart',
        iconColor: theme.palette.secondary.main,
        iconBg: subtle(theme.palette.secondary.main),
      };
    case 'metric_card':
      return {
        Icon: SpeedRounded,
        typeLabel: 'Metric',
        iconColor: theme.palette.success.main,
        iconBg: subtle(theme.palette.success.main),
      };
    case 'finding':
      return {
        Icon: PushPinRounded,
        typeLabel: 'Finding',
        iconColor: theme.palette.warning.main,
        iconBg: subtle(theme.palette.warning.main),
      };
    default:
      return {
        Icon: InsertChartOutlined,
        typeLabel: 'Result',
        iconColor: theme.palette.text.secondary,
        iconBg: alpha(theme.palette.text.primary, 0.08),
      };
  }
}

// localStorage keys
const CHAT_COLLAPSED_KEY = 'klaris.copilot.chatCollapsed';
const CHAT_WIDTH_KEY = 'klaris.copilot.chatWidth';
const CHAT_WIDTH_DEFAULT = 420;
const CHAT_WIDTH_MIN = 320;
const CHAT_WIDTH_MAX = 720;

// ─── Keyboard shortcuts overlay ─────────────────────────────────────────────
const KEYBOARD_SHORTCUTS: Array<{ keys: string[]; label: string }> = [
  { keys: ['?'], label: 'Show this help' },
  { keys: ['Cmd', '\\'], label: 'Toggle focus mode (hide chat)' },
  { keys: ['['], label: 'Previous result' },
  { keys: [']'], label: 'Next result' },
  { keys: ['Esc'], label: 'Close dialogs / overlays' },
];

function KeyboardShortcutsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <AppDialog open={open} onClose={onClose} size="xs" title="Keyboard shortcuts">
      <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, display: 'block', mb: 1.25 })}>
        Press <KeyboardKey>?</KeyboardKey> anywhere in Copilot to toggle this panel.
      </Typography>
      <Stack divider={<Box sx={(t) => ({ borderTop: `1px solid ${t.tokens.border.subtle}` })} />} spacing={0}>
        {KEYBOARD_SHORTCUTS.map((s, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              py: 1,
            }}
          >
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>
              {s.label}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              {s.keys.map((k, j) => (
                <KeyboardKey key={j}>{k}</KeyboardKey>
              ))}
            </Box>
          </Box>
        ))}
      </Stack>
    </AppDialog>
  );
}

function KeyboardKey({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="span"
      sx={(t) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 22,
        height: 22,
        px: 0.875,
        borderRadius: 0.75,
        bgcolor: t.tokens.surface.sunken,
        border: `1px solid ${t.tokens.border.subtle}`,
        color: t.tokens.text.secondary,
        fontFamily: t.typography.fontFamilyMono,
        fontSize: '0.6875rem',
        fontWeight: 600,
      })}
    >
      {children}
    </Box>
  );
}

export default function CopilotPage() {
  const theme = useTheme();
  const isMobileCopilot = useMediaQuery(theme.breakpoints.down('md'));
  const { threadId: urlThreadId } = useParams<{ threadId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { notify } = React.useContext(SnackbarContext);
  const { shouldShow: onboardingActive, currentStepId } = useProductOnboarding();
  /** First-time “pin widget” onboarding only: redirect + dashboard coach mark, not on every add. */
  const onboardingFirstWidgetStep =
    onboardingActive && currentStepId === 'widget_added';
  const [mobilePanel, setMobilePanel] = useState<MobileCopilotPanel>('chat');
  const [mobileResultsView, setMobileResultsView] = useState<MobileResultsView>('list');
  const [mobileDetailArtifactId, setMobileDetailArtifactId] = useState<string | null>(null);

  // Store selectors (explicit types for strict TS)
  const threadId = useCopilotStore((s: any) => s.threadId) as string | null;
  const setThreadId = useCopilotStore((s: any) => s.setThreadId) as (id: string | null) => void;
  const messages = useCopilotStore((s: any) => s.messages) as CopilotMessage[];
  const isStreaming = useCopilotStore((s: any) => s.isStreaming) as boolean;
  const streamingContent = useCopilotStore((s: any) => s.streamingContent) as string;
  const streamingAction = useCopilotStore((s: any) => s.streamingAction) as string;
  const pendingSQLApproval = useCopilotStore((s: any) => s.pendingSQLApproval) as SQLApproval | null;
  const threadTitle = useCopilotStore((s: any) => s.threadTitle) as string | null;
  const inputText = useCopilotStore((s: any) => s.inputText) as string;
  const setInputText = useCopilotStore((s: any) => s.setInputText) as (text: string) => void;

  // Workspace - active artifact resolved here, navigation is in sidebar
  const workspaceTabs = useCopilotStore((s: any) => s.workspaceTabs) as WorkspaceArtifact[];
  const activeTabId = useCopilotStore((s: any) => s.activeTabId) as string | null;
  const setActiveTab = useCopilotStore((s: any) => s.setActiveTab) as (id: string | null) => void;
  const activeArtifact = workspaceTabs.find((t) => t.id === activeTabId) || null;

  // ── Focus mode (collapse chat panel) ─────────────────────────────────────
  const [chatCollapsed, setChatCollapsed] = useState<boolean>(() => {
    try { return window.localStorage.getItem(CHAT_COLLAPSED_KEY) === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { window.localStorage.setItem(CHAT_COLLAPSED_KEY, chatCollapsed ? '1' : '0'); }
    catch { /* ignore */ }
  }, [chatCollapsed]);
  const toggleChatCollapsed = useCallback(() => setChatCollapsed((v) => !v), []);

  // ── Resizable chat panel ─────────────────────────────────────────────────
  const [chatWidth, setChatWidth] = useState<number>(() => {
    try {
      const raw = window.localStorage.getItem(CHAT_WIDTH_KEY);
      const v = raw ? parseInt(raw, 10) : NaN;
      if (Number.isFinite(v)) return Math.min(CHAT_WIDTH_MAX, Math.max(CHAT_WIDTH_MIN, v));
    } catch { /* ignore */ }
    return CHAT_WIDTH_DEFAULT;
  });
  useEffect(() => {
    try { window.localStorage.setItem(CHAT_WIDTH_KEY, String(chatWidth)); }
    catch { /* ignore */ }
  }, [chatWidth]);
  const dragStateRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const onResizeStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    dragStateRef.current = { startX: e.clientX, startWidth: chatWidth };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [chatWidth]);
  const onResizeMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const ds = dragStateRef.current;
    if (!ds) return;
    const dx = e.clientX - ds.startX;
    // Dragging left grows chat (chat is on the right), so subtract dx.
    const next = Math.min(
      CHAT_WIDTH_MAX,
      Math.max(CHAT_WIDTH_MIN, ds.startWidth - dx),
    );
    setChatWidth(next);
  }, []);
  const onResizeEnd = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStateRef.current) return;
    dragStateRef.current = null;
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);
  const onResizeDoubleClick = useCallback(() => {
    setChatWidth(CHAT_WIDTH_DEFAULT);
  }, []);

  // ── Deep-link to a specific artifact via ?artifact=<id> ──────────────────
  const deepLinkAppliedRef = useRef(false);
  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    const params = new URLSearchParams(location.search);
    const aid = params.get('artifact');
    if (!aid) return;
    const found = workspaceTabs.find((t) => t.id === aid);
    if (found) {
      setActiveTab(aid);
      deepLinkAppliedRef.current = true;
    }
  }, [location.search, workspaceTabs, setActiveTab]);

  // ── Keyboard shortcut help overlay (toggled with '?') ────────────────────
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false);

  // ── Keyboard shortcuts: Cmd+\\ (focus mode), [, ] (cycle artifacts), ? ───
  useEffect(() => {
    if (isMobileCopilot) return;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isTyping = !!target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      );
      const meta = e.metaKey || e.ctrlKey;

      // Cmd/Ctrl + \\ — toggle focus mode (works even while typing)
      if (meta && e.key === '\\') {
        e.preventDefault();
        toggleChatCollapsed();
        return;
      }
      // ? — show keyboard shortcut help (only when not typing)
      if (!isTyping && !meta && !e.altKey && e.key === '?') {
        e.preventDefault();
        setShortcutHelpOpen((v) => !v);
        return;
      }
      // Esc — close help overlay
      if (e.key === 'Escape' && shortcutHelpOpen) {
        e.preventDefault();
        setShortcutHelpOpen(false);
        return;
      }
      // [, ] — cycle through artifacts when not typing
      if (!isTyping && (e.key === '[' || e.key === ']') && !meta && !e.altKey) {
        if (workspaceTabs.length === 0) return;
        e.preventDefault();
        const idx = activeTabId
          ? workspaceTabs.findIndex((t) => t.id === activeTabId)
          : -1;
        const nextIdx = e.key === ']'
          ? (idx + 1) % workspaceTabs.length
          : (idx <= 0 ? workspaceTabs.length - 1 : idx - 1);
        const next = workspaceTabs[nextIdx];
        if (next) setActiveTab(next.id);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isMobileCopilot, toggleChatCollapsed, workspaceTabs, activeTabId, setActiveTab, shortcutHelpOpen]);

  // ── Add to Dashboard state ────────────────────────────────────────────────
  const [addToDashboardOpen, setAddToDashboardOpen] = useState(false);
  const [pendingArtifact, setPendingArtifact] = useState<WorkspaceArtifact | null>(null);
  const [widgetName, setWidgetName] = useState('');
  const [dashboards, setDashboards] = useState<Array<{ dashboard_id: string; name: string; description?: string; created_at: string }>>([]);
  const [dashboardsLoading, setDashboardsLoading] = useState(false);
  const [dashboardsError, setDashboardsError] = useState<string | null>(null);
  const [selectedDashboardId, setSelectedDashboardId] = useState<string | null>(null);
  const [newDashboardName, setNewDashboardName] = useState('');
  const [addingWidget, setAddingWidget] = useState(false);
  const [addWidgetError, setAddWidgetError] = useState<string | null>(null);

  const fetchDashboards = useCallback(async () => {
    const token = localStorage.getItem('klaris_jwt');
    const tenantStr = localStorage.getItem('klaris_tenant');
    if (!token || !tenantStr) return;
    let tenantId: string;
    try { tenantId = JSON.parse(tenantStr).tenant_id; } catch { tenantId = tenantStr; }
    setDashboardsLoading(true);
    setDashboardsError(null);
    try {
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setDashboards(data.dashboards || []);
      } else {
        throw new Error('Failed to fetch dashboards');
      }
    } catch (e: any) {
      setDashboardsError(e.message || 'Failed to fetch dashboards');
    } finally {
      setDashboardsLoading(false);
    }
  }, []);

  const handleAddToDashboard = useCallback((artifact: WorkspaceArtifact) => {
    setPendingArtifact(artifact);
    const defaultName = artifact.type === 'chart' ? 'Chart' : artifact.type === 'metric_card' ? 'Metric' : 'Table Results';
    setWidgetName(artifact.title || defaultName);
    setSelectedDashboardId(null);
    setNewDashboardName('');
    setAddWidgetError(null);
    setAddToDashboardOpen(true);
    fetchDashboards();
  }, [fetchDashboards]);

  const submitAddToDashboard = useCallback(async () => {
    const token = localStorage.getItem('klaris_jwt');
    if (!token || !pendingArtifact || !threadId) return;
    setAddingWidget(true);
    setAddWidgetError(null);
    try {
      const tenantStr = localStorage.getItem('klaris_tenant');
      let tenantId: string;
      try { tenantId = JSON.parse(tenantStr!).tenant_id; } catch { tenantId = tenantStr!; }

      let dashboardId = selectedDashboardId;
      // Create new dashboard if name provided
      if (newDashboardName.trim()) {
        const createResp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name: newDashboardName.trim() }),
        });
        if (!createResp.ok) throw new Error('Failed to create dashboard');
        const data = await createResp.json();
        dashboardId = data.dashboard_id;
      }
      if (!dashboardId) {
        setAddWidgetError('Please select or create a dashboard');
        return;
      }
      // Build render_payload from artifact
      let renderPayload: Record<string, any>;
      if (pendingArtifact.type === 'chart') {
        const ca = pendingArtifact as ChartArtifact;
        renderPayload = { type: 'echarts', spec: ca.spec };
      } else if (pendingArtifact.type === 'metric_card') {
        const ma = pendingArtifact as MetricArtifact;
        renderPayload = {
          type: 'metric_card',
          metric_name: ma.metricName,
          metric_value: ma.metricValue,
          metadata: { thread_id: threadId, event_id: ma.eventId },
        };
      } else {
        const qt = pendingArtifact as QueryResultTab;
        renderPayload = {
          type: 'table_result',
          data: {
            columns: qt.columns,
            preview_rows: qt.rows,
            row_count: qt.rowCount,
            frame_id: qt.id,
          },
          display_hint: 'table',
          metadata: { thread_id: threadId, artifact_id: qt.id },
        };
      }
      const widgetResp = await fetch(buildApiUrl(`${tenantApiPrefix(tenantId)}/chat-v2/copilot/dashboards/${dashboardId}/widgets`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: widgetName || pendingArtifact.title || 'Widget',
          thread_id: threadId,
          artifact_id: pendingArtifact.id,
          widget_type: pendingArtifact.type,
          render_payload: renderPayload,
        }),
      });
      if (!widgetResp.ok) {
        const err = await widgetResp.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to add widget');
      }
      setAddToDashboardOpen(false);
      trackWidgetAdded(dashboardId);
      // Await so the backend has persisted `widget_added` before we navigate;
      // the dashboard page refetches onboarding state on route change, and
      // beating that write to the punch is what was leaving the checklist
      // stuck on “Add a widget to a dashboard” after a successful pin.
      await markProductStep('widget_added', { dashboard_id: dashboardId });
      if (onboardingFirstWidgetStep) {
        notify('Added to dashboard! Redirecting to dashboard...', 'success');
        try {
          localStorage.setItem('klaris_nudge_edit_agent', '1');
        } catch {
          /* ignore */
        }
        setTimeout(() => {
          navigate(`/dashboard/${dashboardId}`);
        }, 1200);
      } else {
        notify('Added to dashboard.', 'success');
      }
    } catch (e: any) {
      setAddWidgetError(e.message || 'Failed to add widget');
    } finally {
      setAddingWidget(false);
    }
  }, [
    pendingArtifact,
    selectedDashboardId,
    newDashboardName,
    widgetName,
    threadId,
    notify,
    navigate,
    onboardingFirstWidgetStep,
  ]);

  // Stream hook
  const { sendMessage, sendSQLApproval, stopStream } = useCopilotStream();

  // History hook - loads thread list + replays history when URL has threadId
  useCopilotHistory(urlThreadId || null);

  // Sync threadId from URL param (CopilotPage is always rendered at /copilot/:threadId)
  useEffect(() => {
    if (urlThreadId && threadId !== urlThreadId) {
      setThreadId(urlThreadId);
    } else if (!urlThreadId) {
      // Safety: if somehow rendered without threadId, redirect to landing
      navigate('/copilot', { replace: true });
    }
  }, [urlThreadId, threadId, setThreadId, navigate]);

  // Mobile: new thread → start on Chat + Results list
  useEffect(() => {
    setMobilePanel('chat');
    setMobileResultsView('list');
    setMobileDetailArtifactId(null);
  }, [urlThreadId]);

  useEffect(() => {
    if (isMobileCopilot && mobilePanel === 'workspace') {
      setMobileResultsView('list');
      setMobileDetailArtifactId(null);
    }
  }, [isMobileCopilot, mobilePanel]);

  // Send message
  const handleSend = useCallback(() => {
    if (!inputText.trim() || isStreaming) return;
    const token = window.localStorage.getItem('klaris_jwt');
    if (!token) return;
    sendMessage(inputText.trim(), token);
  }, [inputText, isStreaming, sendMessage]);

  // Auto-send pre-filled text from landing page (so user doesn't need to press Enter again)
  const autoSentRef = useRef(false);
  const pendingAutoSend = useCopilotStore((s) => s.pendingAutoSend);
  useEffect(() => {
    if (autoSentRef.current) return;
    if (!pendingAutoSend) return;
    if (inputText.trim() && messages.length === 0 && !isStreaming) {
      autoSentRef.current = true;
      useCopilotStore.getState().setPendingAutoSend(false);
      const token = window.localStorage.getItem('klaris_jwt');
      if (token) {
        sendMessage(inputText.trim(), token);
      }
    }
  }, [pendingAutoSend, inputText, messages.length, isStreaming, sendMessage]);

  // Suggested prompt click
  const handleSuggestedPrompt = useCallback(
    (prompt: string) => {
      const token = window.localStorage.getItem('klaris_jwt');
      if (!token) return;
      sendMessage(prompt, token);
    },
    [sendMessage],
  );

  // SQL approval handlers
  const handleSQLApprove = useCallback(() => {
    const token = window.localStorage.getItem('klaris_jwt');
    if (!token) return;
    sendSQLApproval('approve', token);
  }, [sendSQLApproval]);

  const handleSQLEdit = useCallback(
    (editedSql: string) => {
      const token = window.localStorage.getItem('klaris_jwt');
      if (!token) return;
      sendSQLApproval('edit', token, editedSql);
    },
    [sendSQLApproval],
  );

  const handleSQLReject = useCallback(
    (reason: string) => {
      const token = window.localStorage.getItem('klaris_jwt');
      if (!token) return;
      sendSQLApproval('reject', token, undefined, reason);
    },
    [sendSQLApproval],
  );

  const workspacePanel = (
    <WorkspacePanel
      artifact={activeArtifact}
      pendingSQLApproval={pendingSQLApproval}
      onSQLApprove={handleSQLApprove}
      onSQLEdit={handleSQLEdit}
      onSQLReject={handleSQLReject}
      onAddToDashboard={handleAddToDashboard}
      threadTitle={threadTitle}
      chatCollapsed={!isMobileCopilot && chatCollapsed}
      onToggleChat={!isMobileCopilot ? toggleChatCollapsed : undefined}
      isStreaming={isStreaming}
      streamingAction={streamingAction}
    />
  );

  const chatPanel = (
    <ChatPanel
      layout={isMobileCopilot ? 'unified' : 'side'}
      messages={messages}
      isStreaming={isStreaming}
      streamingContent={streamingContent}
      streamingAction={streamingAction}
      pendingSQLApproval={!!pendingSQLApproval}
      threadTitle={threadTitle}
      inputText={inputText}
      onInputChange={setInputText}
      onSend={handleSend}
      onStop={stopStream}
      onSuggestedPrompt={handleSuggestedPrompt}
      onSQLApprove={handleSQLApprove}
      onSQLEdit={handleSQLEdit}
      onSQLReject={handleSQLReject}
      onAddToDashboard={handleAddToDashboard}
      panelWidth={chatWidth}
    />
  );

  const mobileDetailArtifact =
    mobileDetailArtifactId != null
      ? workspaceTabs.find((t) => t.id === mobileDetailArtifactId) ?? null
      : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {isMobileCopilot && (
        <Tabs
          value={mobilePanel}
          onChange={(_e, v: MobileCopilotPanel) => setMobilePanel(v)}
          variant="fullWidth"
          sx={{
            flexShrink: 0,
            minHeight: 42,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 42,
              py: 0.75,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.88rem',
            },
          }}
        >
          <Tab value="chat" label="Chat" />
          <Tab
            value="workspace"
            label={
              pendingSQLApproval ? (
                <Badge color="warning" variant="dot" sx={{ pr: 0.5, '& .MuiBadge-badge': { top: 4 } }}>
                  Results
                </Badge>
              ) : (
                'Results'
              )
            }
          />
        </Tabs>
      )}

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'row',
          minHeight: 0,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        {isMobileCopilot ? (
          <>
            <Box
              sx={{
                display: mobilePanel === 'workspace' ? 'flex' : 'none',
                flex: 1,
                flexDirection: 'column',
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              {mobileResultsView === 'detail' && mobileDetailArtifact ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      flexShrink: 0,
                      borderBottom: 1,
                      borderColor: 'divider',
                      px: 0.5,
                      py: 0.25,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => {
                        setMobileResultsView('list');
                        setMobileDetailArtifactId(null);
                      }}
                      aria-label="Back to results list"
                    >
                      <ArrowBackIosNewRounded sx={{ fontSize: 18 }} />
                    </IconButton>
                    <Typography variant="subtitle2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                      {mobileDetailArtifact.title}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <ArtifactCanvas
                      artifact={mobileDetailArtifact}
                      onAddToDashboard={handleAddToDashboard}
                      showSql={false}
                      embedTitleInCard
                    />
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    px: 1.5,
                    py: 2,
                    bgcolor: (t) => (t.palette.mode === 'dark' ? alpha(t.palette.common.black, 0.2) : alpha(t.palette.grey[500], 0.06)),
                  }}
                >
                  {workspaceTabs.length === 0 ? (
                    <Stack alignItems="center" sx={{ py: 6, px: 2, textAlign: 'center' }}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                          bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.12 : 0.08),
                        }}
                      >
                        <InsertChartOutlined sx={{ fontSize: 32, color: 'primary.main', opacity: 0.9 }} />
                      </Box>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        No results yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 280, lineHeight: 1.5 }}>
                        Ask a question in Chat to generate tables, charts, and metrics here.
                      </Typography>
                    </Stack>
                  ) : (
                    <Stack spacing={1.25}>
                      {workspaceTabs.map((tab) => {
                        const meta = mobileArtifactCardMeta(tab, theme);
                        const Icon = meta.Icon;
                        return (
                          <Card
                            key={tab.id}
                            variant="outlined"
                            elevation={0}
                            sx={(t) => ({
                              borderRadius: `${t.ds.radius.md}px`,
                              borderColor: t.tokens.border.subtle,
                              bgcolor: t.tokens.surface.raised,
                              boxShadow: t.tokens.shadow.sm,
                              transition: `transform ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}, box-shadow ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
                              '&:active': { transform: 'scale(0.99)' },
                            })}
                          >
                            <CardActionArea
                              onClick={() => {
                                setMobileDetailArtifactId(tab.id);
                                setMobileResultsView('detail');
                                setActiveTab(tab.id);
                              }}
                              sx={(t) => ({ borderRadius: `${t.ds.radius.md}px` })}
                            >
                              <CardContent
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5,
                                  py: 1.75,
                                  px: 1.5,
                                  '&:last-child': { pb: 1.75 },
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 2,
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: meta.iconBg,
                                  }}
                                >
                                  <Icon sx={{ fontSize: 26, color: meta.iconColor }} />
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight={600}
                                    sx={{
                                      lineHeight: 1.35,
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    {tab.title}
                                  </Typography>
                                  <Chip
                                    label={meta.typeLabel}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      mt: 0.75,
                                      height: 22,
                                      fontSize: '0.68rem',
                                      fontWeight: 600,
                                      color: 'text.secondary',
                                      borderColor: alpha(theme.palette.divider, 0.45),
                                      bgcolor:
                                        theme.palette.mode === 'dark'
                                          ? alpha(theme.palette.common.white, 0.06)
                                          : alpha(theme.palette.grey[500], 0.06),
                                      '& .MuiChip-label': { px: 0.9, color: 'inherit' },
                                    }}
                                  />
                                </Box>
                                <ChevronRightRounded sx={{ fontSize: 22, color: 'text.disabled', flexShrink: 0 }} />
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              )}
            </Box>
            <Box
              sx={{
                display: mobilePanel === 'chat' ? 'flex' : 'none',
                flex: 1,
                flexDirection: 'column',
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              {chatPanel}
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>{workspacePanel}</Box>
            {!chatCollapsed && (
              <Box
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize chat panel"
                title="Drag to resize · double-click to reset"
                onPointerDown={onResizeStart}
                onPointerMove={onResizeMove}
                onPointerUp={onResizeEnd}
                onPointerCancel={onResizeEnd}
                onDoubleClick={onResizeDoubleClick}
                sx={(t) => ({
                  flexShrink: 0,
                  width: 6,
                  cursor: 'col-resize',
                  position: 'relative',
                  bgcolor: 'transparent',
                  transition: `background-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
                  '&:hover': {
                    bgcolor: alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.18 : 0.1),
                  },
                  '&:active': {
                    bgcolor: alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.28 : 0.16),
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: '50%',
                    width: 1,
                    bgcolor: t.tokens.border.subtle,
                    transform: 'translateX(-0.5px)',
                  },
                })}
              />
            )}
            {!chatCollapsed && chatPanel}
          </>
        )}
      </Box>

      <KeyboardShortcutsDialog
        open={shortcutHelpOpen}
        onClose={() => setShortcutHelpOpen(false)}
      />

      <AddToDashboardModal
        open={addToDashboardOpen}
        onClose={() => !addingWidget && setAddToDashboardOpen(false)}
        dashboards={dashboards}
        loading={dashboardsLoading}
        error={dashboardsError}
        selectedDashboardId={selectedDashboardId}
        setSelectedDashboardId={setSelectedDashboardId}
        newDashboardName={newDashboardName}
        setNewDashboardName={setNewDashboardName}
        widgetName={widgetName}
        setWidgetName={setWidgetName}
        onSubmit={submitAddToDashboard}
        creating={addingWidget}
        submitError={addWidgetError}
      />
    </Box>
  );
}
